import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// logger
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
// order-entry
import { OEModelGetterService } from "./oe-model-getter.service";
import { OEModelLockingService } from "./oe-model-locking.service";
import { OEUtilityService } from "../basic/oe-utility.service";
import { OEDataModel } from "../../data/models/oe-data.model";
import { OENOperationMode } from "../../data/enums/oe-operation-mode.enum";
import { OEModelSetterService } from "./oe-model-setter.service";
import { OENLockProfileType } from "../../data/enums/oe-modify-lock-profile-type.enum";
import { OETimeMachineService } from "../basic/oe-time-machine.service";
import { ICGoodFromUntilModel } from "../../interface/controls/models/bfx-good-from-until.model";
import {
  ICAutocompleteGenericModel,
  ICSuspendModel,
} from "../../interface/controls/models/bfx-interface-controls.models";
import { OENLockedState } from "../../data/enums/oe-locked-state.enum";
import { OENPanel } from "../../data/enums/oe-panel.enum";
import {
  OEInputLockStateItemModel,
  OELockingProfileModel,
} from "../../data/models/oe-locking-profile.model";
import { OENInputControl } from "../../data/enums/oe-input-control.enum";
import { OEStrategyModel, OEStrategyRefModel } from "../../data/models/oe-strategy.model";
import { OEAdapterReverseService } from "../adapter/oe-adapter-reverse.service";
import { OELookupService } from "../basic/oe-lookup.service";
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { OEInputModel } from "../../data/models/oe-input.model";
import { OEDLoadingDataModel } from "../../data/models/oe-loading-data.model";

// import { modelGroupProvider } from '@angular/forms/directives/ng_model_group';//MARKED FOR REMOVAL WHEN UPGRADING ANGULAR
import { OECoordinatorService } from "../oe-coordinator.service";
import { OELoggerService } from "../basic/oe-logger.service";
import { ClientAccountVisibilityType } from "shared/cubiq-services";
import { OENDisplayState } from "app/order-entry/data/enums/oe-display-state.enum";
import { ConstraintValue } from "../../../../shared/cubiq-services/model/core/ConstraintValue";

@Injectable()
export class OEModelOperationsService {
  /**********************************************************************/
  /* CONSTRUCTOR*/
  constructor(
    private oeLogger: OELoggerService,
    private getter: OEModelGetterService,
    private setter: OEModelSetterService,
    private locker: OEModelLockingService,
    private lookup: OELookupService,
    private utility: OEUtilityService,
    private coordinator: OECoordinatorService,
    private adapterReverse: OEAdapterReverseService,
    private timeMachine: OETimeMachineService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
  }

  /**********************************************************************/
  /* ADD NEW MODEL*/

  /**
   * NOTE: Used only for modification (for now)
   */
  // CHANGE: addOrderEntryModel
  /**
   * Convert the payload from an order query (that contains full strategy details) into a list
   * of OE Data models that are added to the master data service.
   *
   * @param rootOrderData
   * @param orderId
   * @param mode
   * @param resRefId
   * @returns
   */
  public addInstance(
    rootOrderData: CubiqTS.OrderDataImpl,
    orderId: string,
    mode: OENOperationMode,
    resRefId: string = null,
  ): Promise<string> {
    return new Promise<string>((resolve) => {
      try {
        if (rootOrderData != null && this.getter.validateRefFormat(rootOrderData.clOrdID)) {
          this.adapterReverse
            .adaptOrderDataToOrderEntryModel(
              rootOrderData,
              mode,
              this.getter.instanceNull(resRefId),
            )
            .then(
              (modelsToAdd: Array<OEDataModel>) => {
                let rootRefId: string = null;
                if (modelsToAdd != null && Array.isArray(modelsToAdd) && modelsToAdd.length > 0) {
                  let createdModelRefIds: Array<string> = new Array<string>();
                  // create models
                  modelsToAdd.forEach((model: OEDataModel) => {
                    if (
                      model != null &&
                      model instanceof OEDataModel &&
                      this.getter.validateRefFormat(model.refId)
                    ) {
                      try {
                        let refId: string =
                          (mode !== OENOperationMode.MODIFY &&
                            mode !== OENOperationMode.MODIFY_SCRATCHPAD) ||
                          (resRefId != null && resRefId !== model.refId)
                            ? this.addEmptyInstance(model.refId)
                            : model.refId;
                        if (this.getter.has(refId) && model.refId === refId) {
                          if (mode == OENOperationMode.MONITOR_EXECUTION) {
                            // Ensure display state remains unchanged when updating - it may be OK without the above guard
                            const oldDisplayState = this.getter.getDisplayState(model.refId);
                            model.displayState = oldDisplayState;
                          }
                          this.setter.update(refId, model);
                          this.locker.createLockingProfileForNewEntry(refId);

                          // set root
                          if (rootRefId === null) {
                            rootRefId = refId;
                            model.strategy.rootRefId = null;
                          }
                          this.coordinator.cmdAddStrategyLegRefListener(rootRefId, refId);

                          // set display mode
                          this.setter.setOperationMode(refId, mode);
                          //
                          createdModelRefIds.push(refId);
                        } else {
                          logger.out.error(
                            `ERROR: oeOperations.addInstance(): Invalid model entered, to be deleted! Model dump: %O`,
                            model,
                          );
                          this.deleteInstance(model.refId);
                        }
                      } catch (ex) {
                        logger.out.error(
                          `EXCEPTION: oeOperations.addInstance(): Model creation failed! Exception: %O`,
                          ex,
                        );
                      }
                    } else {
                      logger.out.error(
                        `ERROR: oeOperations.addInstance(): Parsing produced invalid model! Model dump: %O`,
                        model,
                      );
                    }
                  });

                  // create relationships
                  this.createNewRelationships(createdModelRefIds, rootOrderData);
                  // handle locking
                  createdModelRefIds.forEach((refId: string) => {
                    switch (mode) {
                      case OENOperationMode.MODIFY:
                        this.locker.processLockingOnModify(
                          refId,
                          rootOrderData.orderId.toString(),
                          orderId,
                        );
                        break;
                      case OENOperationMode.MODIFY_INFO:
                        this.locker.processLockingOnModifyInfo(refId, orderId);
                        break;
                      case OENOperationMode.EDIT:
                      case OENOperationMode.DUPLICATE:
                      case OENOperationMode.MODIFY_SCRATCHPAD:
                        break;
                      case OENOperationMode.AMEND_MARKUP:
                        this.locker.createLockProfileForModification(
                          refId,
                          OENLockProfileType.AMEND_MARKUP_LOCK,
                        );
                        break;
                      case OENOperationMode.MONITOR_EXECUTION:
                        break;
                      default:
                        logger.out.error(`ERROR: oeOperations.addInstance(): unknown mode ${mode}`);
                        break;
                    }
                    // privatization locking
                    if (mode !== OENOperationMode.DUPLICATE && mode !== OENOperationMode.EDIT) {
                      this.locker.processLockingForPrivatization(refId);
                    }
                  });

                  resolve(rootRefId);
                } else {
                  logger.out.error(`ERROR: oeOperations.addInstance(): No models to add!`);
                  resolve(null);
                }
              },
              (reason) => {
                logger.out.error(
                  `ERROR: oeOperations.addInstance(): OrderData parsing fail: ${reason}`,
                );
                resolve(null);
              },
            );
        } else {
          logger.out.error(
            `ERROR: oeOperations.addInstance(): Order Data is null or invalid RefId! OrderData dump: %O`,
            rootOrderData,
          );
          resolve(null);
        }
      } catch (ex) {
        logger.out.error(`EXCEPTION: oeOperations.addInstance(): %O`, ex);
        resolve(null);
      }
    });
  }

  private createNewRelationships(
    createdModelRefIds: string[],
    rootOrderData: CubiqTS.OrderDataImpl,
  ) {
    createdModelRefIds.forEach((refId: string) => {
      if (this.getter.has(refId)) {
        try {
          let nodeRelationships = this.adapterReverse.findRelationshipsForRefId(
            refId,
            rootOrderData,
          );
          this.oeLogger.outputDevLogToConsole(
            `DEV-TEST: relationships for nodeID: ${refId} | relationships: `,
            false,
            nodeRelationships,
          );

          if (nodeRelationships != null && nodeRelationships.length > 0) {
            nodeRelationships.forEach((relationship: OEStrategyRefModel) => {
              this.createStrategyRelationship(refId, relationship.refId, relationship.type);
            });
          }
        } catch (ex) {
          logger.out.error(
            `EXCEPTION: oeOperations.addInstance(): Relationship creation failed! Exception: %O`,
            ex,
          );
        }
      } else {
        logger.out.error(
          `ERROR: oeOperations.addInstance(): model with refId: ${refId} is invalid in dataMap!`,
        );
      }
    });
  }

  /**********************************************************************/
  /* NEW */
  /**
   * REGISTER new entry model
   */
  // CHANGE: registerNewOrderEntryInstance
  public addEmptyInstance(refId: string = null): string {
    let dataModel: OEDataModel = new OEDataModel();
    dataModel.refId = refId != null ? refId : this.getter.generateRandomRefId();
    this.setter.set(dataModel.refId, dataModel);
    return dataModel.refId;
  }

  public addEmptyInstanceType(entryType: OENEntryType, refId: string = null): string {
    let dataModel: OEDataModel = new OEDataModel();
    dataModel.refId = refId ?? this.getter.generateRandomRefId();
    dataModel.entryType = entryType;
    if (entryType === OENEntryType.ALGO || entryType === OENEntryType.NDF_ALGO) {
      dataModel.orderType = this.utility.createAutocompleteForOrderType(
        CubiqTS.RequestOrderType.ALGO,
      );
    } else if (entryType === OENEntryType.BENCHMARK && dataModel.strategy != null) {
      dataModel.strategy.strategyType = CubiqTS.StrategyType.BMARK;
    } else if (entryType === OENEntryType.GAMMA && dataModel.strategy != null) {
      dataModel.strategy.strategyType = CubiqTS.StrategyType.GAMMA;
    }
    this.setter.set(dataModel.refId, dataModel);
    return dataModel.refId;
  }
  /**********************************************************************/
  /* DELETE */
  /**
   * DESTROY model
   */
  // be careful when using this method, it does not cancel the ccyPair rate subscription
  // to fully delete the model and cancel subscription use the actions service methods

  // CHANGE: deleteOrderEntryInstance
  public deleteInstance(refId: string): void {
    if (this.getter.has(refId)) {
      this.setter.delete(refId);
    } else {
      logger.out.error(
        `ERROR: BfxOrderEntryModelService.deleteOrderEntryInstance(): Invalid RefId: '${refId}'`,
      );
    }
  }
  /**********************************************************************/
  /* RESET */
  //CHANGE: resetOrderEntryInstance
  public resetInstance(refId: string): void {
    if (this.getter.has(refId)) {
      this.getter.get(refId).resetDataModel();
    } else {
      logger.out.error(
        `ERROR: BfxOrderEntryModelService.resetOrderEntryInstance(): Invalid RefId '${refId}'`,
      );
    }
  }

  /**********************************************************************/
  /* COPY */

  // CHANGE: copyOrderEntryInstanceWithStrategies()
  public copyInstanceStrategies(
    refId: string,
    newRefId: string = null,
    applyVisibilityConsideration: boolean = false,
  ): string {
    // returns rootRefId
    if (this.getter.hasStrategy(refId)) {
      let copyRefIds: Array<string> = new Array<string>();
      try {
        let originalRootRefId: string = this.getter.get(refId).strategy.isRoot
          ? refId
          : this.getter.get(refId).strategy.rootRefId;
        let copyMap: Map<string, string> = new Map<string, string>();
        let originalRefIds: Array<string> = this.getter.getStrategyNodesListRoot(originalRootRefId);

        // create model copies
        originalRefIds.forEach((originalRefId) => {
          let copyRefId = this.copyInstance(
            originalRefId,
            newRefId != null && originalRefId == refId ? newRefId : null,
            applyVisibilityConsideration,
          );
          copyRefIds.push(copyRefId);
          copyMap.set(originalRefId, copyRefId);
        });

        if (copyRefIds.length === 1) {
          this.locker.createLockProfileForStrategyModel(
            copyRefIds[0],
            CubiqTS.OrderRelationshipType.NONE,
          );
        } else {
          // establish relationships for copies
          originalRefIds.forEach((originalRefId) => {
            let originalModel = this.getter.get(originalRefId);
            let copyRefId = copyMap.get(originalRefId);
            if (this.getter.hasStrategy(originalRefId) && this.getter.has(copyRefId)) {
              // copy child relationships
              if (originalModel.strategy.children.length > 0) {
                originalModel.strategy.children.forEach((originalChildRelationship) => {
                  let copyChildRefId = copyMap.get(originalChildRelationship.refId);
                  if (this.getter.has(copyChildRefId)) {
                    let childRelationshipDoesNotExist =
                      this.getter
                        .get(copyRefId)
                        .strategy.children.map((child) => {
                          return child.refId;
                        })
                        .find((localChildRefId) => {
                          return localChildRefId === copyChildRefId;
                        }) == null;
                    if (childRelationshipDoesNotExist) {
                      this.createStrategyRelationship(
                        copyRefId,
                        copyChildRefId,
                        originalChildRelationship.type,
                      );
                    }
                  }
                });
              }
              // copy sibling relationships
              if (originalModel.strategy.siblings.length > 0) {
                // only one sibling possible
                originalModel.strategy.siblings.forEach((originalBroRelationship) => {
                  let copyBroRefId = copyMap.get(originalBroRelationship.refId);
                  if (this.getter.has(copyBroRefId)) {
                    let broRelationshipDoesNotExist =
                      this.getter
                        .get(copyRefId)
                        .strategy.siblings.map((bro) => {
                          return bro.refId;
                        })
                        .find((localChildRefId) => {
                          return localChildRefId === copyBroRefId;
                        }) == null;
                    if (broRelationshipDoesNotExist) {
                      this.createStrategyRelationship(
                        copyRefId,
                        copyBroRefId,
                        originalBroRelationship.type,
                      );
                    }
                    /// DEV-DEBUG
                    let originalRootId =
                      originalModel.strategy.rootRefId == null
                        ? originalModel.refId
                        : originalModel.strategy.rootRefId;
                    let copyActualRootId =
                      this.getter.get(copyRefId).strategy.rootRefId == null
                        ? copyRefId
                        : this.getter.get(copyRefId).strategy.rootRefId;
                    let copyExpectedRootId = copyMap.get(originalRootId);
                    this.oeLogger.outputDevLogToConsole(
                      `DEV-SCRATCHPAD: RELATIONSHIP: Original ROOT [${originalRootId}] | New ROOT [${copyActualRootId}] | Map [${originalRefId}] -> [${copyRefId}] | Root ${
                        copyActualRootId === copyExpectedRootId ? "GOOD" : "DISASTER"
                      }`,
                    );
                    ///
                  }
                });
              }
            }
          });
        }

        return copyMap.get(refId);
      } catch (ex) {
        throw new Error('Copy Order Entry instance with strategy failed! exception="' + ex + '"');
      }
    } else if (this.getter.has(refId)) {
      // Where's there's only a single order instance, we copy it and ensure everything is unlocked -
      const retId = this.copyInstance(refId, newRefId, applyVisibilityConsideration);
      this.locker.createLockProfileForStrategyModel(retId, CubiqTS.OrderRelationshipType.NONE);
      return retId;
    } else {
      throw new Error('Invalid Order Entry instance source, RefId "' + refId + '" not found!');
    }
  }

  /**
   * COPY new entry in getter
   */
  public copyInstance(
    refId: string,
    newReferenceId: string = null,
    applyVisibilityConsideration: boolean = false,
  ): string {
    if (this.getter.has(refId)) {
      let newRefId: string = null;
      try {
        newRefId = this.addEmptyInstanceType(this.getter.getEntryType(refId), newReferenceId);
        let existing = this.getter.get(refId);

        let newItem = existing.clone();
        this.sanityCheckGoodFromUntil(newItem.goodFrom);
        this.sanityCheckGoodFromUntil(newItem.goodUntil);
        newItem.refId = newRefId;
        newItem.orderId = null;
        newItem.requestModel = null;
        //newItem.displayState = BfxOrderEntryOrderStateDisplayEnum.SHOW_ORDER_ENTRY;
        newItem.subscriptionLifeCycleActive = false;
        newItem.subscriptionInterventionActive = false;
        newItem.toleranceAccept = null;
        newItem.toleranceAmountAccept = null;
        // VISIBILITY - CLIENT
        if (
          (applyVisibilityConsideration &&
            newItem.client instanceof CubiqTS.ClientImpl &&
            !ClientAccountVisibilityType.isFull(newItem.client)) ||
          newItem.client == null
        ) {
          newItem.client = null;
          newItem.clientTrader = null;
          newItem.contacts = null;
          // comments
          newItem.clientComment.value = null;
          newItem.clientComment.defaultComment = null;
          newItem.clientComment.manualComment = null;
          newItem.clientComment.clientTraderComment = null;
          newItem.privateComments = null;
        }
        // VISIBILITY - ACCOUNT
        if (
          applyVisibilityConsideration &&
          newItem.account instanceof CubiqTS.AccountImpl &&
          !ClientAccountVisibilityType.isFull(newItem.account)
        ) {
          newItem.account = null;
        }

        // needed because market orders require locking profile
        newItem.strategy.lockProfile =
          existing.strategy instanceof OEStrategyModel &&
          existing.strategy.lockProfile instanceof OELockingProfileModel &&
          !(
            this.getter.isOperationModeModifyInfo(refId) ||
            this.getter.isOperationModeAmendMarkup(refId) ||
            this.getter.isOperationModeMonitorExecution(refId)
          )
            ? this.locker.cloneProfile(existing.strategy.lockProfile)
            : new OELockingProfileModel();

        this.setter.update(newRefId, newItem);

        return newRefId;
      } catch (ex) {
        if (newRefId != null && this.getter.has(newRefId)) {
          this.deleteInstance(newRefId);
        }
        throw new Error("Copy Order Entry instance failed! ex: " + ex);
      }
    } else {
      throw new Error('Invalid Order Entry instance source, RefId "' + refId + '" not found!');
    }
  }

  /**
   * This motherfucking method
   */
  // CHANGE: regenerateRefIdsWhereNoOrderId
  public regenerateRefs(refId: string): Map<string, string> {
    try {
      let modifiedRefIdsMap: Map<string, string> = new Map<string, string>(); // MAP<originalRefId, newRefId>
      let allRefIds: Array<string> = this.getter.getStrategyNodesListRoot(refId);
      // change refIds for nodes without orderId
      allRefIds.forEach((originalRefId: string) => {
        this.setter.resetRequestModel(originalRefId); // request model reset, why? just because
        if (
          this.getter.has(originalRefId) &&
          (this.getter.getOrderId(originalRefId) == null || this.getter.isClone(originalRefId))
        ) {
          if (!this.getter.isRootNode(originalRefId)) {
            let newRefId = this.getter.generateRandomRefId();
            modifiedRefIdsMap.set(originalRefId, newRefId);
            logger.out.debug(
              `DEBUG: _oeModel: RefID regeneration :  OLD= '${originalRefId}' => '${newRefId}' =NEW`,
            );
            // change refId key
            let newModel = Object.assign(new OEDataModel(), this.getter.get(originalRefId));
            newModel.refId = newRefId;
            newModel.orderId = null;
            newModel.orderRef = null;
            this.setter.set(newRefId, newModel);
            //delete this.dataMap[originalRefId];
          } else {
            logger.out.error(
              `ERROR: _oeModel.regenerateRefIdsWhereNoOrderId(): Trying to regenerate refId ('${refId}'), refId not on root!`,
            );
          }
        }
      });
      // apply changed refIds on strategy relationships
      let originalRefIds: Array<string> = Array.from(modifiedRefIdsMap.keys());
      if (originalRefIds != null && originalRefIds.length > 0) {
        // check if any changes at all
        allRefIds.forEach((itemRefId) => {
          // loop all original nodes
          // check children
          let children = this.getter.get(itemRefId).strategy.children;
          if (children != null && children.length > 0) {
            originalRefIds.forEach((originalRefId: string) => {
              let indexToRemove: number = children
                .map((item) => {
                  return item.refId;
                })
                .indexOf(originalRefId);
              let newRefId: string = modifiedRefIdsMap.get(originalRefId);
              if (indexToRemove >= 0 && newRefId != null) {
                let relType: CubiqTS.OrderRelationshipType = children[indexToRemove].type;
                this.getter.get(itemRefId).strategy.children.splice(indexToRemove, 1, {
                  refId: newRefId,
                  type: relType,
                });
              }
            });
          }
          // check siblings
          let siblings = this.getter.get(itemRefId).strategy.siblings;
          if (siblings != null && siblings.length > 0) {
            originalRefIds.forEach((originalRefId: string) => {
              let indexToRemove: number = siblings
                .map((item) => {
                  return item.refId;
                })
                .indexOf(originalRefId);
              let newRefId: string = modifiedRefIdsMap.get(originalRefId);
              if (indexToRemove >= 0 && newRefId != null) {
                let relType: CubiqTS.OrderRelationshipType = siblings[indexToRemove].type;
                this.getter.get(itemRefId).strategy.siblings.splice(indexToRemove, 1, {
                  refId: newRefId,
                  type: relType,
                });
              }
            });
          }
          // check parent
          let parentRefId: string = this.getter.get(itemRefId).strategy.parentRefId;
          if (parentRefId != null) {
            originalRefIds.forEach((originalRefId: string) => {
              if (parentRefId === originalRefId) {
                this.setRootAndParent(modifiedRefIdsMap.get(originalRefId), itemRefId);
              }
            });
          }
        });
        return modifiedRefIdsMap;
      } else {
        return null;
      }
    } catch (ex) {
      logger.out.error(`EXCEPTION: _oeModel.regenerateRefIdsWhereNoOrderId(): %O`, ex);
      return null;
    }
  }

  /**********************************************************************/
  /* COMPLEX MODEL UPDATES  */

  public updateClientTrader(
    refId: string,
    _clientTrader: ICAutocompleteGenericModel | CubiqTS.UserImpl,
  ): OEInputModel | null {
    try {
      if (this.getter.has(refId) && this.getter.get(refId).clientTrader !== _clientTrader) {
        let userCode = null;
        let oldUserCode = null;
        let user = null;
        // reset manual client comment on all clientTrader changes as per KD and Tina
        //this.getter.resetManualClientComment(refId);

        if (_clientTrader instanceof ICAutocompleteGenericModel) {
          userCode = _clientTrader.value.userCode;
        } else if (_clientTrader instanceof CubiqTS.UserImpl) {
          userCode = _clientTrader.userCode;
          user = _clientTrader;
        }

        if (this.getter.get(refId).clientTrader instanceof ICAutocompleteGenericModel) {
          oldUserCode = (this.getter.get(refId).clientTrader as ICAutocompleteGenericModel).value
            .userCode;
        } else if (this.getter.get(refId).clientTrader instanceof CubiqTS.UserImpl) {
          oldUserCode = (this.getter.get(refId).clientTrader as CubiqTS.UserImpl).userCode;
        }

        this.setter.setClientTrader(refId, _clientTrader as any);

        if (
          this.getter.get(refId).clientTrader == null ||
          (_clientTrader != null && userCode !== oldUserCode && userCode != null)
        ) {
          if (user == null) {
            user = this.lookup.userNow(userCode);
          }
          let clientTraderComment =
            user != null && user.comment != null && user.comment.length > 0
              ? `${user.userName}: ${user.comment}`
              : null;
          this.setter.setClientTraderClientComment(refId, clientTraderComment);
          return new OEInputModel(
            OENPanel.PANEL_MORE,
            OENInputControl.CLIENT_COMMENTS,
            this.getter.getClientComment(refId),
          );
        }
      }
      return null;
    } catch (ex) {
      logger.out.error("ERROR: oeOperations.updateClientTrader(): exception: ", ex.message);
    }
  }

  public updateClient(refId: string, _clientData: CubiqTS.ClientImpl | null): void {
    try {
      if (this.getter.has(refId) && this.getter.get(refId).client !== _clientData) {
        // object equality not always assured in strategy context
        // check id before updating comments
        let currentClient = this.getter.get(refId).client;
        let isClientChange =
          currentClient == null ||
          (_clientData != null && currentClient.clientId !== _clientData.clientId);

        this.setter.setClient(refId, _clientData);

        if (
          this.getter.get(refId).orderType != null &&
          this.getter.get(refId).orderType.value === CubiqTS.RequestOrderType.TP
        ) {
          // if TP update state of the allowFillsCheckbox
          //	this.coordinator.cmdShowAllowPartialFillsCheckbox(refId);
        }

        if (isClientChange) {
          this.setter.setDefaultComments(
            refId,
            _clientData.defaultClientComment,
            _clientData.defaultPrivateComment,
          );
          //this.getter.resetManualClientComment(refId);
          this.setter.setExecutionMode(
            refId,
            _clientData.autoFillEnabled
              ? CubiqTS.ExecutionType.AUTOMATIC
              : CubiqTS.ExecutionType.MANUAL,
          );
        }
      }
      //this.coordinator.cmdPropagateClientChange(refId);
    } catch (ex) {
      logger.out.error("ERROR: oeOperations.updateClient(): exception: ", ex.message);
    }
  }

  /**********************************************************************/
  /*********************** STRATEGIES ***********************************/
  /**********************************************************************/

  /**********************************************************************/
  /*  STRATEGY - Drop  */

  // CHANGE: dropStrategyModelChildren();
  public dropChildren(refId: string): void {
    if (this.getter.has(refId)) {
      let model = this.getter.get(refId);
      model.strategy.children = [];
    } else {
      throw new Error(`Invalid refId ${refId} for dropStrategyModelChildren()`);
    }
  }

  public dropSiblings(refId: string) {
    if (this.getter.has(refId)) {
      let model = this.getter.get(refId);

      model.strategy.siblings = [];
    } else {
      throw new Error(`Invalid refId ${refId} for dropStrategyModelSiblings()`);
    }
  }

  /**********************************************************************/
  /*  STRATEGY - ADD Relationships  */

  public addRelationshipTimeout(parentRefId: string, legRefId: string): void {
    if (this.getter.has(legRefId) && this.getter.has(parentRefId)) {
      if (
        this.getter.isRelationshipAllowedTimeout(legRefId) &&
        this.getter.isRelationshipAllowedTimeout(parentRefId)
      ) {
        this.createStrategyRelationship(
          parentRefId,
          legRefId,
          CubiqTS.OrderRelationshipType.ON_TIMEOUT,
        );
      } else {
        throw new Error(
          `On_Timeout not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} have existing siblings.`,
        );
      }
    } else {
      throw new Error(
        `On_Timeout not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} not found.`,
      );
    }
  }

  public addRelationshipLoop(parentRefId: string, legRefId: string): void {
    if (this.getter.has(legRefId) && this.getter.has(parentRefId)) {
      if (
        this.getter.isRelationshipAllowedLoop(legRefId) &&
        this.getter.isRelationshipAllowedLoop(parentRefId)
      ) {
        this.createStrategyRelationship(parentRefId, legRefId, CubiqTS.OrderRelationshipType.LOOP);
      } else {
        throw new Error(
          `Loop not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} have existing siblings.`,
        );
      }
    } else {
      throw new Error(
        `Loop not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} not found.`,
      );
    }
  }

  public addRelationshipIfDone(parentRefId: string, legRefId: string): void {
    if (this.getter.has(legRefId) && this.getter.has(parentRefId)) {
      if (
        this.getter.isRelationshipAllowedIfDone(parentRefId) &&
        this.getter.isRelationshipAllowedIfDone(legRefId)
      ) {
        this.createStrategyRelationship(
          parentRefId,
          legRefId,
          CubiqTS.OrderRelationshipType.IFDONE,
        );
      } else {
        throw new Error(
          `IfDone not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} have existing siblings.`,
        );
      }
    } else {
      throw new Error(
        `IfDone not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} not found.`,
      );
    }
  }

  public addRelationshipOco(parentRefId: string, legRefId: string): void {
    if (this.getter.has(legRefId) && this.getter.has(parentRefId)) {
      if (
        this.getter.isRelationshipAllowedOco(legRefId) &&
        this.getter.isRelationshipAllowedOco(parentRefId)
      ) {
        this.createStrategyRelationship(parentRefId, legRefId, CubiqTS.OrderRelationshipType.OCO);
      } else {
        throw new Error(
          `Oco not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} have existing siblings.`,
        );
      }
    } else {
      throw new Error(
        `Oco not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} not found.`,
      );
    }
  }

  protected createStrategyRelationship(
    parentRefId: string,
    legRefId: string,
    relationshipType: CubiqTS.OrderRelationshipType,
  ): void {
    if (this.getter.has(legRefId) && this.getter.has(parentRefId)) {
      let legModel = this.getter.get(legRefId);
      let parentModel = this.getter.get(parentRefId);
      switch (relationshipType) {
        case CubiqTS.OrderRelationshipType.OCO:
          // check and create siblings
          if (!Array.isArray(legModel.strategy.siblings))
            legModel.strategy.siblings = new Array<OEStrategyRefModel>();
          // check and create siblings
          if (!Array.isArray(legModel.strategy.children))
            legModel.strategy.children = new Array<OEStrategyRefModel>();
          // check and establish relationship(duplex)
          if (
            legModel.strategy.siblings.length === 0 &&
            parentModel.strategy.siblings.length === 0
          ) {
            legModel.strategy.siblings.push({
              refId: parentModel.refId,
              type: CubiqTS.OrderRelationshipType.OCO,
            });
            parentModel.strategy.siblings.push({
              refId: legModel.refId,
              type: CubiqTS.OrderRelationshipType.OCO,
            });
            // set parent and root for leg
            this.setRootAndParent(parentRefId, legRefId);
            // create and attach interface locking profile for strategyLeg
            this.locker.createLockProfileForStrategyModel(
              legRefId,
              CubiqTS.OrderRelationshipType.OCO,
            );
            // update permissions - parent
            this.updateRelationshipPermission(
              parentRefId,
              CubiqTS.OrderRelationshipType.OCO,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              parentRefId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.LOCKED,
            );
            // update permissions - sibling
            this.updateRelationshipPermission(
              legRefId,
              CubiqTS.OrderRelationshipType.OCO,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              legRefId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.LOCKED,
            );
          } else if (
            legModel.strategy.siblings.length === 1 &&
            parentModel.strategy.siblings.length === 1 &&
            legModel.strategy.siblings[0].type === CubiqTS.OrderRelationshipType.OCO &&
            legModel.strategy.siblings[0].refId === parentModel.refId &&
            parentModel.strategy.siblings[0].type === CubiqTS.OrderRelationshipType.OCO &&
            parentModel.strategy.siblings[0].refId === legModel.refId
          ) {
            // relationship already properly established - re-update permissions only (locking profile update removed because of OMS-36294)

            // update permissions - parent
            this.updateRelationshipPermission(
              parentRefId,
              CubiqTS.OrderRelationshipType.OCO,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              parentRefId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.LOCKED,
            );
            // update permissions - sibling
            this.updateRelationshipPermission(
              legRefId,
              CubiqTS.OrderRelationshipType.OCO,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              legRefId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.LOCKED,
            );
          } else {
            throw new Error(
              `Oco not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} have existing siblings.`,
            );
          }
          break;
        case CubiqTS.OrderRelationshipType.IFDONE:
          // check and create siblings
          if (!Array.isArray(legModel.strategy.siblings))
            legModel.strategy.siblings = new Array<OEStrategyRefModel>();
          // check and create children
          if (!Array.isArray(legModel.strategy.children))
            legModel.strategy.children = new Array<OEStrategyRefModel>();
          // establish relationship(simplex)
          parentModel.strategy.children.push({
            refId: legModel.refId,
            type: CubiqTS.OrderRelationshipType.IFDONE,
          });
          // set parent and root for leg
          this.setRootAndParent(parentRefId, legRefId);
          // create and attach interface locking profile for strategyLeg
          this.locker.createLockProfileForStrategyModel(
            legRefId,
            CubiqTS.OrderRelationshipType.IFDONE,
          );
          // update permissions - parent only
          this.updateRelationshipPermission(
            parentRefId,
            CubiqTS.OrderRelationshipType.LOOP,
            OENLockedState.LOCKED,
          );
          break;
        case CubiqTS.OrderRelationshipType.ON_TIMEOUT:
          // check and create siblings
          if (!Array.isArray(legModel.strategy.siblings))
            legModel.strategy.siblings = new Array<OEStrategyRefModel>();
          // check and create siblings
          if (!Array.isArray(legModel.strategy.children))
            legModel.strategy.children = new Array<OEStrategyRefModel>();
          // establish relationship(simplex)
          parentModel.strategy.children.push({
            refId: legModel.refId,
            type: CubiqTS.OrderRelationshipType.ON_TIMEOUT,
          });
          // set parent and root for leg
          this.setRootAndParent(parentRefId, legRefId);
          // create and attach interface locking profile for strategyLeg
          this.locker.createLockProfileForStrategyModel(
            legRefId,
            CubiqTS.OrderRelationshipType.ON_TIMEOUT,
          );
          // update permissions - Parent only
          this.updateRelationshipPermission(
            parentRefId,
            CubiqTS.OrderRelationshipType.LOOP,
            OENLockedState.LOCKED,
          );
          break;
        case CubiqTS.OrderRelationshipType.LOOP:
          // check and create siblings on leg
          if (!Array.isArray(legModel.strategy.siblings))
            legModel.strategy.siblings = new Array<OEStrategyRefModel>();
          // check and create children on leg
          if (!Array.isArray(legModel.strategy.children))
            legModel.strategy.children = new Array<OEStrategyRefModel>();
          // check and establish relationship(duplex)
          if (
            legModel.strategy.siblings.length === 0 &&
            parentModel.strategy.siblings.length === 0
          ) {
            legModel.strategy.siblings.push({
              refId: parentModel.refId,
              type: CubiqTS.OrderRelationshipType.LOOP,
            });
            parentModel.strategy.siblings.push({
              refId: legModel.refId,
              type: CubiqTS.OrderRelationshipType.LOOP,
            });
            // set parent and root for leg
            this.setRootAndParent(parentRefId, legRefId);
            // create and attach interface locking profile for strategyLeg
            this.locker.createLockProfileForStrategyModel(
              legRefId,
              CubiqTS.OrderRelationshipType.LOOP,
            );
            // update permissions Parent
            this.updateRelationshipPermission(
              parentRefId,
              CubiqTS.OrderRelationshipType.OCO,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              parentRefId,
              CubiqTS.OrderRelationshipType.IFDONE,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              parentRefId,
              CubiqTS.OrderRelationshipType.ON_TIMEOUT,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              parentRefId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.LOCKED,
            );
            // update permissions Parent
            this.updateRelationshipPermission(
              legRefId,
              CubiqTS.OrderRelationshipType.OCO,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              legRefId,
              CubiqTS.OrderRelationshipType.IFDONE,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              legRefId,
              CubiqTS.OrderRelationshipType.ON_TIMEOUT,
              OENLockedState.LOCKED,
            );
            this.updateRelationshipPermission(
              legRefId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.LOCKED,
            );
          } else {
            throw new Error(
              `Oco not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} have existing siblings.`,
            );
          }
          break;
        default:
          logger.out.error(
            `ERROR: oeOperations.createStrategyRelationship(): Unknown relationship type '${relationshipType}'`,
          );
          break;
      }
    } else {
      throw new Error(
        `Oco not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} not found or no strategy model.`,
      );
    }
  }

  protected setRootAndParent(parentRefId: string, legRefId: string): void {
    if (this.getter.has(legRefId) && this.getter.has(parentRefId)) {
      let legModel = this.getter.get(legRefId);
      let parentModel = this.getter.get(parentRefId);
      // set parent
      legModel.strategy.parentRefId = parentRefId;
      // set root
      if (this.getter.isRootNode(parentRefId)) {
        legModel.strategy.rootRefId = parentRefId;
        if (parentModel.strategy.parentRefId == null) {
          // if parent is root and parent pointer is not set => first relationship for root, ie the root looses virginity :)
          // set parent to itself since it is not set
          parentModel.strategy.parentRefId = parentRefId;
          // create and attach interface locking profile for parent
          this.locker.createLockProfileForStrategyModel(
            parentRefId,
            CubiqTS.OrderRelationshipType.NONE,
          );
        }
      } else if (parentModel.strategy.rootRefId != null) {
        legModel.strategy.rootRefId = parentModel.strategy.rootRefId;
      } else {
        // in theory this should never happen
        throw new Error(
          `Root relationships not set! This should not happen! Parent is not root, but doesn't know the root!`,
        );
      }
    } else {
      throw new Error(
        `Parent and Root relationships not set! Model legRefId: ${legRefId} or parentRefId: ${parentRefId} not found or no strategy models.`,
      );
    }
  }

  /**********************************************************************/
  /*  STRATEGY - REMOVE Relationships  */

  public removeSiblingRelationship(refId: string, relationshipIndex: number): void {
    this.removeStrategyRelationship(refId, relationshipIndex, false);
  }

  public removeChildRelationship(refId: string, relationshipIndex: number): void {
    this.removeStrategyRelationship(refId, relationshipIndex, true);
  }

  private removeStrategyRelationship(
    refId: string,
    relationshipIndex: number,
    isChildRelationship: boolean,
  ) {
    if (this.getter.hasStrategy(refId) && relationshipIndex >= 0) {
      let parentModel = this.getter.get(refId);
      // get the relationship type
      let relationshipType: CubiqTS.OrderRelationshipType = isChildRelationship
        ? parentModel.strategy.children[relationshipIndex].type
        : parentModel.strategy.siblings[relationshipIndex].type;
      // remove relationship
      if (isChildRelationship) {
        parentModel.strategy.children.splice(relationshipIndex, 1);
      } else {
        parentModel.strategy.siblings.splice(relationshipIndex, 1);
      }
      // update permissions
      switch (relationshipType) {
        case CubiqTS.OrderRelationshipType.OCO:
          if (!this.getter.hasSiblings(refId)) {
            // allow oco
            this.updateRelationshipPermission(
              refId,
              CubiqTS.OrderRelationshipType.OCO,
              OENLockedState.UNLOCKED,
            );
          }
          if (
            !this.getter.hasSiblings(refId) &&
            !this.getter.hasChildren(refId) &&
            (parentModel.orderType == null ||
              parentModel.orderType.value == null ||
              !this.isTSL(parentModel.orderType.value))
          ) {
            this.updateRelationshipPermission(
              refId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.UNLOCKED,
            );
          }
          break;
        case CubiqTS.OrderRelationshipType.IFDONE:
          if (
            !this.getter.hasSiblings(refId) &&
            !this.getter.hasChildren(refId) &&
            (parentModel.orderType == null ||
              parentModel.orderType.value == null ||
              !this.isTSL(parentModel.orderType.value))
          ) {
            this.updateRelationshipPermission(
              refId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.UNLOCKED,
            );
          }
          break;
        case CubiqTS.OrderRelationshipType.ON_TIMEOUT:
          if (
            !this.getter.hasSiblings(refId) &&
            !this.getter.hasChildren(refId) &&
            (parentModel.orderType == null ||
              parentModel.orderType.value == null ||
              !this.isTSL(parentModel.orderType.value))
          ) {
            this.updateRelationshipPermission(
              refId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.UNLOCKED,
            );
          }
          break;
        case CubiqTS.OrderRelationshipType.LOOP:
          this.updateRelationshipPermission(
            refId,
            CubiqTS.OrderRelationshipType.IFDONE,
            OENLockedState.UNLOCKED,
          );
          this.updateRelationshipPermission(
            refId,
            CubiqTS.OrderRelationshipType.ON_TIMEOUT,
            OENLockedState.UNLOCKED,
          );
          if (!this.getter.hasSiblings(refId)) {
            // allow oco
            this.updateRelationshipPermission(
              refId,
              CubiqTS.OrderRelationshipType.OCO,
              OENLockedState.UNLOCKED,
            );
          }
          if (
            !this.getter.hasSiblings(refId) &&
            !this.getter.hasChildren(refId) &&
            (parentModel.orderType == null ||
              parentModel.orderType.value == null ||
              !this.isTSL(parentModel.orderType.value))
          ) {
            this.updateRelationshipPermission(
              refId,
              CubiqTS.OrderRelationshipType.LOOP,
              OENLockedState.UNLOCKED,
            );
          }
          break;
        default:
          throw new Error(`Relationship type ${relationshipType} invalid`);
      }
    } else {
      throw new Error(`Relationship not removed! Model with refId: ${refId} not found.`);
    }
  }

  public updateRelationshipPermission(
    refId: string,
    relType: CubiqTS.OrderRelationshipType,
    permission: OENLockedState,
  ): boolean {
    if (this.getter.has(refId)) {
      let model = this.getter.get(refId);
      switch (relType) {
        case CubiqTS.OrderRelationshipType.IFDONE:
          if (permission === OENLockedState.UNLOCKED) {
            model.strategy.permissions.allowIfDone = true;
            model.strategy.lockProfile.updateInputLockState(
              new OEInputLockStateItemModel(
                OENPanel.PANEL_NOTIFICATIONS,
                OENInputControl.BTN_IF_DONE,
                OENLockedState.UNLOCKED,
              ),
            );
          } else if (permission === OENLockedState.LOCKED) {
            model.strategy.permissions.allowIfDone = false;
            model.strategy.lockProfile.updateInputLockState(
              new OEInputLockStateItemModel(
                OENPanel.PANEL_NOTIFICATIONS,
                OENInputControl.BTN_IF_DONE,
                OENLockedState.LOCKED,
              ),
            );
          } else {
            logger.out.error(
              `ERROR: oeOperations.updateRelationshipPermission(): Unknown permission state on IFDONE!`,
            );
            return false;
          }
          break;
        case CubiqTS.OrderRelationshipType.OCO:
          if (permission === OENLockedState.UNLOCKED) {
            model.strategy.permissions.allowOco = true;
            model.strategy.lockProfile.updateInputLockState(
              new OEInputLockStateItemModel(
                OENPanel.PANEL_NOTIFICATIONS,
                OENInputControl.BTN_OCO,
                OENLockedState.UNLOCKED,
              ),
            );
          } else if (permission === OENLockedState.LOCKED) {
            model.strategy.permissions.allowOco = false;
            model.strategy.lockProfile.updateInputLockState(
              new OEInputLockStateItemModel(
                OENPanel.PANEL_NOTIFICATIONS,
                OENInputControl.BTN_OCO,
                OENLockedState.LOCKED,
              ),
            );
          } else {
            logger.out.error(
              `ERROR: oeOperations.updateRelationshipPermission(): Unknown permission state on OCO!`,
            );
            return false;
          }
          break;
        case CubiqTS.OrderRelationshipType.LOOP:
          if (permission === OENLockedState.UNLOCKED) {
            model.strategy.permissions.allowLoop = true;
            model.strategy.lockProfile.updateInputLockState(
              new OEInputLockStateItemModel(
                OENPanel.PANEL_NOTIFICATIONS,
                OENInputControl.BTN_LOOP,
                OENLockedState.UNLOCKED,
              ),
            );
          } else if (permission === OENLockedState.LOCKED) {
            model.strategy.permissions.allowLoop = false;
            model.strategy.lockProfile.updateInputLockState(
              new OEInputLockStateItemModel(
                OENPanel.PANEL_NOTIFICATIONS,
                OENInputControl.BTN_LOOP,
                OENLockedState.LOCKED,
              ),
            );
          } else {
            logger.out.error(
              `ERROR: oeOperations.updateRelationshipPermission(): Unknown permission state on LOOP!`,
            );
            return false;
          }
          break;
        case CubiqTS.OrderRelationshipType.ON_TIMEOUT:
          if (permission === OENLockedState.UNLOCKED) {
            model.strategy.permissions.allowTimeout = true;
            model.strategy.lockProfile.updateInputLockState(
              new OEInputLockStateItemModel(
                OENPanel.PANEL_NOTIFICATIONS,
                OENInputControl.BTN_TIMEOUT,
                OENLockedState.UNLOCKED,
              ),
            );
          } else if (permission === OENLockedState.LOCKED) {
            model.strategy.permissions.allowTimeout = false;
            model.strategy.lockProfile.updateInputLockState(
              new OEInputLockStateItemModel(
                OENPanel.PANEL_NOTIFICATIONS,
                OENInputControl.BTN_TIMEOUT,
                OENLockedState.LOCKED,
              ),
            );
          } else {
            logger.out.error(
              `ERROR: oeOperations.updateRelationshipPermission(): Unknown permission state on ON_TIMEOUT!`,
            );
            return false;
          }
          break;
        default:
          logger.out.error(
            `ERROR: oeOperations.updateRelationshipPermission(): Unknown relationship type on permission update!`,
          );
          return false;
      }
      return true;
    } else {
      return false;
    }
  }

  /**********************************************************************/
  /* STRATEGY - Model Data Transformations */
  /**
   * Strategy graph rules
   * - all nodes have parents, each node has a single parent
   * - the root node is its own parent, the rest of the nodes have parents other then themselves
   * - the parent pointer (excluding the root) points either to a sibling or parent (ie. this node is child of the node referenced by the parent pointer)
   * - the 'sibling' relationship is mislabeled in the BCDFX terminology, it should be 'spouse' since the cardinality is one.
   * - a strategy node (leg) can have one sibling and multiple children
   * - 'Sibling' relationships are duplex (two-way), both nodes maintain a relationship object in the siblings arrays
   * - 'Children' relationships are simplex (one-way), only the parent remains a pointer to the child in the childrem relationship  array
   * - the root pointer of the root node is null, for the rest of the nodes the root pointer references the root node
   */
  public invertOrderTypeForStrategy(
    refId: string,
    relationshipType: CubiqTS.OrderRelationshipType,
  ): boolean {
    if (this.getter.has(refId)) {
      let model = this.getter.get(refId);
      switch (relationshipType) {
        case CubiqTS.OrderRelationshipType.OCO:
          if (
            model.orderType != null &&
            model.orderType instanceof ICAutocompleteGenericModel &&
            model.orderType.value != null
          ) {
            switch (model.orderType.value) {
              case CubiqTS.RequestOrderType.TP:
                model.orderType = new ICAutocompleteGenericModel(
                  CubiqTS.RequestOrderType.SL_ATRATE,
                  "SL",
                );
                break;
              case CubiqTS.RequestOrderType.TSL:
              case CubiqTS.RequestOrderType.TSL_ASK:
              case CubiqTS.RequestOrderType.TSL_BID:
                model.orderType = new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TP, "TP");
                break;
              case CubiqTS.RequestOrderType.SL_ATRATE:
              case CubiqTS.RequestOrderType.SL_BID:
              case CubiqTS.RequestOrderType.SL_ASK:
                model.orderType = new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TP, "TP");
                break;
              case CubiqTS.RequestOrderType.CALL:
              case CubiqTS.RequestOrderType.ALGO:
              case CubiqTS.RequestOrderType.BMARK:
                // do nothing;
                break;
              default:
                return false;
            }
            return true;
          } else {
            return false;
          }
        case CubiqTS.OrderRelationshipType.IFDONE:
          if (
            model.orderType != null &&
            model.orderType instanceof ICAutocompleteGenericModel &&
            model.orderType.value != null
          ) {
            switch (model.orderType.value) {
              case CubiqTS.RequestOrderType.TP:
                return this.invertIntent(refId);
              case CubiqTS.RequestOrderType.TSL:
              case CubiqTS.RequestOrderType.TSL_ASK:
              case CubiqTS.RequestOrderType.TSL_BID:
                model.orderType = new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TP, "TP");
                return this.invertIntent(refId);
              case CubiqTS.RequestOrderType.SL_ATRATE:
              case CubiqTS.RequestOrderType.SL_BID:
              case CubiqTS.RequestOrderType.SL_ASK:
                model.orderType = new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TP, "TP");
                return this.invertIntent(refId);
              case CubiqTS.RequestOrderType.CALL:
              case CubiqTS.RequestOrderType.ALGO:
              case CubiqTS.RequestOrderType.BMARK:
                // do nothing
                break;
              default:
                return false;
            }
            return true;
          } else {
            return false;
          }
        case CubiqTS.OrderRelationshipType.ON_TIMEOUT:
          if (
            model.orderType != null &&
            model.orderType instanceof ICAutocompleteGenericModel &&
            model.orderType.value != null
          ) {
            switch (model.orderType.value) {
              case CubiqTS.RequestOrderType.TP:
                return this.invertIntent(refId);
              case CubiqTS.RequestOrderType.TSL:
              case CubiqTS.RequestOrderType.TSL_ASK:
              case CubiqTS.RequestOrderType.TSL_BID:
                model.orderType = new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TP, "TP");
                return this.invertIntent(refId);
              case CubiqTS.RequestOrderType.SL_ATRATE:
              case CubiqTS.RequestOrderType.SL_BID:
              case CubiqTS.RequestOrderType.SL_ASK:
                model.orderType = new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TP, "TP");
                return this.invertIntent(refId);
              case CubiqTS.RequestOrderType.CALL:
              case CubiqTS.RequestOrderType.ALGO:
              case CubiqTS.RequestOrderType.BMARK:
                // do nothing
                break;
              default:
                return false;
            }
            return true;
          } else {
            return false;
          }
        case CubiqTS.OrderRelationshipType.LOOP:
          if (
            model.orderType != null &&
            model.orderType instanceof ICAutocompleteGenericModel &&
            model.orderType.value != null
          ) {
            switch (model.orderType.value) {
              case CubiqTS.RequestOrderType.TP:
                return this.invertIntent(refId);
              case CubiqTS.RequestOrderType.TSL:
              case CubiqTS.RequestOrderType.TSL_ASK:
              case CubiqTS.RequestOrderType.TSL_BID:
                logger.out.error("ERROR: Loop should not be available if the root leg is a TSL. ");
                return false;
              case CubiqTS.RequestOrderType.SL_ATRATE:
              case CubiqTS.RequestOrderType.SL_BID:
              case CubiqTS.RequestOrderType.SL_ASK:
                return this.invertIntent(refId);
              case CubiqTS.RequestOrderType.CALL:
              case CubiqTS.RequestOrderType.ALGO:
              case CubiqTS.RequestOrderType.BMARK:
                // do nothing
                break;
              default:
                return false;
            }
            return true;
          } else {
            return false;
          }
        default:
          logger.out.error(
            `ERROR: oeOperations.createStrategyRelationship(): Unknown relationship type '${relationshipType}'`,
          );
          break;
      }
    } else {
      throw new Error(`Invert Order Type not set! Model ref: ${refId} not found.`);
    }
  }

  public clearOrderTypeOnStrategyNodesOfType(
    refId: string,
    orderTypeValue: CubiqTS.RequestOrderType,
  ): void {
    let nodeList: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    if (Array.isArray(nodeList) && nodeList.length > 0) {
      for (let nodeRefId of nodeList) {
        let orderData = this.getter.get(nodeRefId);

        if (
          orderData instanceof OEDataModel &&
          orderData.orderType instanceof ICAutocompleteGenericModel &&
          orderData.orderType.value === orderTypeValue
        ) {
          try {
            this.setter.setOrderType(nodeRefId, null);
          } catch (ex) {
            logger.out.error(
              "ERROR: oeOperations.clearOrderTypeOnStrategyNodesOfType(): exception: %O",
              ex.message,
            );
          }
        }
      }
    }
  }

  public resetCcyPairToRootOnStrategyNodesOfType(
    refId: string,
    orderTypeValue: CubiqTS.RequestOrderType,
  ): void {
    let nodeList: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    if (Array.isArray(nodeList) && nodeList.length > 0) {
      let rootNode = this.getter.getParentModel(refId);
      let rootCcyPair = rootNode.ccyPair;
      let rootCcyPairModel = rootNode.ccyPairModel;
      for (let nodeRefId of nodeList) {
        let orderData = this.getter.get(nodeRefId);

        if (
          orderData instanceof OEDataModel &&
          orderData.orderType instanceof ICAutocompleteGenericModel &&
          orderData.orderType.value === orderTypeValue
        ) {
          try {
            this.setter.setCcyPair(nodeRefId, rootCcyPair, rootCcyPairModel);
          } catch (ex) {
            logger.out.error(
              "ERROR: oeOperations.clearOrderTypeOnStrategyNodesOfType(): exception: %O",
              ex.message,
            );
          }
        }
      }
    }
  }

  public clearSuspendOnStrategyNodesOfType(
    refId: string,
    orderTypeValue: CubiqTS.RequestOrderType,
  ): void {
    let nodeList: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    if (Array.isArray(nodeList) && nodeList.length > 0) {
      for (let nodeRefId of nodeList) {
        let orderData = this.getter.get(nodeRefId);

        if (
          orderData instanceof OEDataModel &&
          orderData.orderType instanceof ICAutocompleteGenericModel &&
          orderData.orderType.value === orderTypeValue &&
          orderData.suspend instanceof ICSuspendModel &&
          orderData.suspend.activated
        ) {
          try {
            let suspendValue = new ICSuspendModel();
            suspendValue.activated = false;
            suspendValue.suspendFrom = null;
            suspendValue.suspendTo = null;
            this.setter.setSuspend(nodeRefId, suspendValue);
          } catch (ex) {
            logger.out.error(
              "ERROR: oeOperations.clearSuspendOnStrategyNodesOfType(): exception: %O",
              ex.message,
            );
          }
        }
      }
    }
  }

  /**
   * FXBFXOM-7802 - invoked on adding a strategy leg, if the
   * copied instance transitions to TP the allow partial fill flag
   * is copied from the selected client.
   *
   * @param parentRefId the source order (parent) reference
   * @param copyRef the copied order (new leg) reference
   */
  public checkAllowPartialOnCopiedInstance(parentRefId: string, copyRef: string): void {
    if (this.getter.has(parentRefId) && this.getter.has(copyRef)) {
      let model = this.getter.get(parentRefId);
      let copiedModel = this.getter.get(copyRef);

      let fromOrderType = model.orderType?.value;
      let toOrderType = copiedModel.orderType?.value;

      if (
        fromOrderType != CubiqTS.RequestOrderType.TP &&
        toOrderType === CubiqTS.RequestOrderType.TP &&
        model.client != null
      ) {
        copiedModel.allowPartialFills = model.client.allowPartialFillFg
          ? ConstraintValue.FlagEnabled
          : ConstraintValue.FlagDisabled;
      }
    }
  }

  protected invertIntent(refId: string): boolean {
    if (this.getter.has(refId)) {
      let model = this.getter.get(refId);
      if (
        model.clientIntent != null &&
        typeof model.clientIntent === "number" &&
        model.clientIntent >= 0 &&
        model.clientIntent <= 1
      ) {
        if (model.clientIntent === CubiqTS.OrderIntent.BUY) {
          model.clientIntent = CubiqTS.OrderIntent.SELL; // Buy TP -> Sell TP
        } else if (model.clientIntent === CubiqTS.OrderIntent.SELL) {
          model.clientIntent = CubiqTS.OrderIntent.BUY; // Sell TP -> Buy TP
        } else {
          return false;
        }
        return true;
      } else {
        return false;
      }
    }
  }

  /**********************************************************************/
  /* HELPER */

  private sanityCheckGoodFromUntil(item: ICGoodFromUntilModel): void {
    if (item != null && item.marketValue != null) {
      let mktConst: CubiqTS.MarketTimeConstantImpl = this.timeMachine.getMarketTimeConstantImpl(
        item.marketValue,
      );
      if (mktConst != null) {
        if (
          CubiqTS.MarketTimeConstantType.TODAY === mktConst.constantType ||
          (CubiqTS.MarketTimeConstantType.DATE === mktConst.constantType &&
            mktConst.constantDate == null)
        ) {
          item.dateValue = CubiqTS.moment().format("DD-MMM-YYYY");
        } else if (CubiqTS.MarketTimeConstantType.TOMORROW === mktConst.constantType) {
          item.dateValue = CubiqTS.moment().add(1, "d").format("DD-MMM-YYYY");
        }
      }
    }
  }

  private isTSL(requestOrderType): boolean {
    switch (requestOrderType) {
      case CubiqTS.RequestOrderType.TSL:
      case CubiqTS.RequestOrderType.TSL_ASK:
      case CubiqTS.RequestOrderType.TSL_BID:
        return true;
      default:
        return false;
    }
  }

  /**
   * When an order has been transferred externally, update the trader group of the order in
   * the model and ensure any displayed model is updated.
   * Only permissable when we are monitoring an order, not in any other view state - in other states, the
   * operation is ignored.
   * @param orderData
   * @returns
   */
  public processExternalTransfer(orderData: CubiqTS.OrderDataImpl): void {
    const refId = orderData.clOrdID;
    if (!orderData || !this.getter.has(refId)) {
      logger.out.info(`omo-s - Cannot find expected order model for orderId ${orderData?.orderId}`);
      return;
    }
    if (!this.getter.monitoringExecution(refId)) {
      return;
    }
    // We have a data model for this order Id - now we must create a loading model for the differences only
    const traderGroup = this.lookup.traderGroupNow(orderData.deskId);
    if (traderGroup) {
      this.setter.setTraderGroup(refId, traderGroup);
      const inputModel = new OEInputModel(
        OENPanel.PANEL_FINANCIALS,
        OENInputControl.TRADER_GROUP,
        traderGroup,
      );
      let loadData = new OEDLoadingDataModel(
        refId,
        this.getter.getEntryType(refId),
        false,
        [inputModel],
        null,
        true,
      );
      this.coordinator.cmdLoadInterfaceControlData(refId, loadData);
    }
  }

  /**
   * Handle an unknown set of updates to a monitored order. This will currently take the existing data model
   * for the order and replace all the values.
   * Once all values have been replaced (we replace eithything regardless of whether it's changed) we
   * send out a new loading model to instruct all view components to reload their values.
   * @param rootOrderData
   * @returns
   */
  public processExternalOrderUpdate(rootOrderData: CubiqTS.OrderDataImpl): Promise<void> {
    const refId = rootOrderData.clOrdID;
    if (!rootOrderData || !this.getter.has(refId)) {
      logger.out.info(
        `omo-s - Cannot find expected order model for order ${rootOrderData.orderId}`,
      );
      return;
    }
    logger.out.info(
      `omo-s - Updating view ticket for order ${rootOrderData.orderRef} after LC event`,
    );
    const mode = this.getter.getOperationMode(refId);
    // const displayState = this.getter.getDisplayState(refId);

    // We have a data model for this order Id - now we must create a loading model

    return new Promise<void>((resolve, reject) => {
      try {
        // So we convert our order data and write into new datamodel - we don't use existing datamodel and overwrite as we need
        // to copy over some data that could otherwise get freshly initialised e.g. operationMode, displayState
        const blankModel = new OEDataModel();
        this.adapterReverse
          .adaptOrderDataToOrderEntryModel(rootOrderData, mode, blankModel)
          .then((modelsToAdd: Array<OEDataModel>) => {
            if (modelsToAdd != null && Array.isArray(modelsToAdd) && modelsToAdd.length > 0) {
              // create models
              modelsToAdd.forEach((model: OEDataModel) => {
                let modelRefId = model.refId;
                if (this.getter.has(modelRefId)) {
                  this.copyNonAdaptedProperties(model, this.getter.get(modelRefId));
                  logger.out.info(
                    `omo-s - Reloading Order ticket details for order ${
                      model.orderId
                    }, displayState=${OENDisplayState[model.displayState]}, operation mode = ${
                      OENOperationMode[model.operationMode]
                    }`,
                  );

                  this.setter.update(modelRefId, model);
                  const loadingData: OEDLoadingDataModel =
                    this.utility.createInterfaceLoadDataFromModel(model);
                  this.coordinator.cmdLoadInterfaceControlData(modelRefId, loadingData);
                }
              });
            }
            resolve();
          });
      } catch (ex) {
        logger.out.error(`EXCEPTION: oeOperations.addInstance(): %O`, ex);
        reject(ex);
      }
    });
  }

  /**
   * THere are several key state flags that need to be copied form an existing data model to a new replica
   * @param newModel
   * @param existingModel
   */
  private copyNonAdaptedProperties(newModel: OEDataModel, existingModel: OEDataModel) {
    newModel.operationMode = existingModel.operationMode;
    newModel.displayState = existingModel.displayState;

    newModel.lockUserId = existingModel.lockUserId;
    newModel.isLockedForModification = existingModel.isLockedForModification;

    newModel.subscriptionInterventionActive = existingModel.subscriptionInterventionActive;
    newModel.subscriptionLifeCycleActive = existingModel.subscriptionLifeCycleActive;
    newModel.subscriptionScratchpadActive = existingModel.subscriptionScratchpadActive;

    newModel.isActivelyDisplayed = existingModel.isActivelyDisplayed;
    newModel.modifyIssuedOrderId = existingModel.modifyIssuedOrderId;
    newModel.strategy = existingModel.strategy;
  }
}
